import { useResetOnClose } from "@/lib/useResetOnClose";
import { BranchTag } from "@/components/BranchTag";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";


export default function Adjustments() {
  const { can } = usePermissions();
  const canCreateMovement = can("STOCK_MOVEMENTS", "create");
  const canPostMovement = can("STOCK_MOVEMENTS", "post");
  const { companies } = useCompany() || {};
  const [adjustments, setAdjustments] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [adjRes, itemRes, whRes] = await Promise.all([api.get("/inventory/stock-adjustments"), api.get("/inventory/items"), api.get("/inventory/warehouses")]);
      setAdjustments(adjRes.data); setItems(itemRes.data); setWarehouses(whRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const itemName = (id) => { const i = items.find((it) => it.id === id); return i ? `${i.code} — ${i.name}` : id; };
  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || id;

  const post = async (adj) => {
    setBusyId(adj.id);
    try {
      await api.post(`/inventory/stock-adjustments/${adj.id}/post`);
      toast.success("Stock adjustment posted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="adjustments-page">
      <PageHeader title="Stock Adjustments" description="Physical count corrections — IN raises stock at an entered cost, OUT reduces it at current average cost." action={canCreateMovement && <CreateAdjustmentDialog companies={companies} items={items} warehouses={warehouses} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && adjustments.length === 0 ? (
          <EmptyState title="No stock adjustments yet" testId="adjustments-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Warehouse</TableHead><TableHead>Direction</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {adjustments.map((a) => (
                <TableRow key={a.id} data-testid={`adjustment-row-${a.id}`}>
                  <TableCell className="text-foreground">{itemName(a.item_id)}<BranchTag row={a} /></TableCell>
                  <TableCell className="text-muted-foreground">{warehouseName(a.warehouse_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{a.direction}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{a.quantity}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-muted-foreground">{a.reason}</TableCell>
                  <TableCell><StatusPill value={a.status} /></TableCell>
                  <TableCell className="text-right">
                    {canPostMovement && a.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === a.id} onClick={() => post(a)} data-testid={`post-adjustment-button-${a.id}`}>
                        <Scale className="h-3.5 w-3.5" /> Post
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateAdjustmentDialog({ companies, items, warehouses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", warehouse_id: "", item_id: "", direction: "IN", quantity: "", unit_cost: "", reason: "", adjustment_date: "" });
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ company_id: "", warehouse_id: "", item_id: "", direction: "IN", quantity: "", unit_cost: "", reason: "", adjustment_date: "" }); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/stock-adjustments", { ...form, unit_cost: form.direction === "IN" ? form.unit_cost : null });
      toast.success("Stock adjustment created");
      setForm({ company_id: "", warehouse_id: "", item_id: "", direction: "IN", quantity: "", unit_cost: "", reason: "", adjustment_date: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-adjustment-button"><Plus className="h-4 w-4" /> New Adjustment</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Stock Adjustment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-adjustment-form">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="adjustment-company-select" />
            <div>
              <Label>Warehouse</Label>
              <Select required value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger data-testid="adjustment-warehouse-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Item</Label>
            <Select required value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
              <SelectTrigger data-testid="adjustment-item-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger data-testid="adjustment-direction-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="IN">IN</SelectItem><SelectItem value="OUT">OUT</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Qty</Label><Input required type="number" step="0.001" data-testid="adjustment-qty-input" value={form.quantity} onChange={set("quantity")} className="mt-1.5" /></div>
            {form.direction === "IN" && <div><Label>Unit cost</Label><Input required type="number" step="0.01" data-testid="adjustment-cost-input" value={form.unit_cost} onChange={set("unit_cost")} className="mt-1.5" /></div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" data-testid="adjustment-date-input" value={form.adjustment_date} onChange={set("adjustment_date")} className="mt-1.5" /></div>
            <div><Label>Reason</Label><Input required data-testid="adjustment-reason-input" value={form.reason} onChange={set("reason")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-adjustment-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
